import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatDate, formatMoney, formatPhoneForReading, toMachineDate } from '@shared/format';
import { pageCountOf, useClampPage, usePageParam } from '@shared/pagination';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import { EmptyState } from '@shared/ui/EmptyState';
import { Pager } from '@shared/ui/pager';
import { RowMenu, type RowMenuEntry } from '@shared/ui/rowmenu';
import { StateBadge } from '@shared/ui/StateBadge';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { SurfaceError } from '../_shared/components';
import { resolveEntitlementSource, resolveSubscriptionState } from '../_shared/constants';
import type { LedgerRow } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { EMPTY_FILTERS, hasAnyFilter, readFilters, writeFilters, type LedgerFilters } from './filters';
import { usePlans } from '../_shared/queries';
import { useCancelSubscription, useRevokeEntitlement } from './mutations';
import { LedgerFilterBar, TermSummary } from './Reporting.LedgerFilters';
import { LEDGER_PAGE_SIZE, subscriptionsKey, useSubscriptions } from './queries';

const TABLE_COLUMNS = 7;

/**
 * The vocabulary member a grant's window resolves to when it has closed. Both
 * halves of the ledger answer their own state, and this is the one token they
 * share.
 */
const ENTITLEMENT_EXPIRED = 'EXPIRED';

/**
 * The states with nothing left to end.
 *
 * A **closed** set, and the check is deliberately one-sided: an unrecognised
 * state keeps its menu rather than losing it. Hiding the action on a token this
 * console has not met yet would remove a control the operator may well be
 * entitled to use, whereas offering one the record cannot take is answered by
 * the owning service with a `409` the error catalogue already names in Arabic.
 * Fail open here, authoritative there.
 */
const ENDED_STATES: ReadonlySet<string> = new Set(['EXPIRED', 'CANCELLED', ENTITLEMENT_EXPIRED]);

const MANUAL_GRANT = 'MANUAL_GRANT';

/**
 * One ending, chosen from a row menu and held until the operator confirms it.
 *
 * The id travels with the choice rather than the row: by the time the dialog
 * commits, the table may have re-fetched under a narrowing that no longer
 * contains the row at all, and re-reading it from the rendered list at that
 * point is how a confirmation ends up acting on a neighbour.
 */
type LedgerAction =
  { kind: 'cancel-now'; subscriptionId: string } | { kind: 'revoke'; entitlementId: string };

/**
 * «أحدث الاشتراكات» — the record-level ledger.
 *
 * Six columns: the four the catalog names — who, which plan, how much, when —
 * plus two the entitlement lifecycle already owns. **المصدر** is there because
 * without it a manual grant would read as a purchase with a missing price, and
 * **الحالة** is the aggregate on the tiles above, at row level.
 *
 * **A manual grant carries no plan and no amount**, and both cells say so: an
 * em-dash with a visually-hidden phrase beside it, so a screen reader is handed
 * a statement rather than a punctuation mark.
 *
 * **The date is the digit boundary in its plainest form** — the human string is
 * Arabic-Indic on the Gregorian calendar and the `datetime` attribute beside it
 * on the same element is ASCII ISO-8601. Every row renders absolute rather than
 * relative: these are dated transactions, and «قبل يومين» on a ledger is a
 * figure nobody can reconcile against a bank statement.
 *
 * **Every row is reachable, and the walk is in the address.** The ledger used to
 * render the service's first page and state its own truncation — «معروض ٩ من ٩»
 * — which answered «how many exist» and left «show me the tenth» with nowhere to
 * go. It now walks the offset paging `backend-finance` already served: the
 * ordinal lives in `?page`, so a page of the ledger is a link, and it is clamped
 * back to a page that exists rather than drawn over the last one's rows.
 *
 * **This is a reading surface** for everything except an ending: granting a new
 * entitlement happens on the user's own detail, and the note above the lookup
 * bar says where.
 *
 * **And it is now searchable, which is the one thing it promised and could not
 * do.** The ledger answers «who bought» — for whoever happened to be on the page
 * being read. Asking it about *a* subscriber meant leaving for the account
 * register, which has no money row on it, so the question could not be answered
 * on either surface. The lookup bar above the table closes that: a term, and the
 * three vocabularies the table already renders as columns.
 *
 * The register link that used to sit alone in this heading has moved into the
 * note — it was never a search control, and with a real one above the table it
 * read as one. That note in turn sits **above** the bar rather than under the
 * pager: both of its sentences are read on the way in, not found on the way
 * out.
 */
export function ReportingLedger() {
  const { page, goToPage } = usePageParam();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);

  const query = useSubscriptions(page, filters);
  const plans = usePlans();
  const queryClient = useQueryClient();
  const toast = useToast();

  const cancelSubscription = useCancelSubscription();
  const revokeEntitlement = useRevokeEntitlement();

  const [pending, setPending] = useState<LedgerAction | null>(null);
  const [confirmSubject, setConfirmSubject] = useState<ConfirmSubject | null>(null);

  const rows = query.data?.items ?? [];
  const pageCount = pageCountOf(query.data?.total, query.data?.page_size ?? LEDGER_PAGE_SIZE);

  useClampPage(page, pageCount);

  /**
   * Apply one facet, keeping the others.
   *
   * `replace` for the term and `push` for a select: typing is one continuous act
   * and should not leave a history entry per keystroke, while choosing a plan is
   * a single decision Back should undo.
   */
  function applyFilter(next: Partial<LedgerFilters>) {
    const isTerm = next.term !== undefined;
    setParams(writeFilters(params, { ...filters, ...next }), { replace: isTerm });
  }

  function clearFilters() {
    setParams(writeFilters(params, EMPTY_FILTERS));
  }

  /** Open the confirmation for one ending, with the words that ending needs. */
  function ask(action: LedgerAction, subscriber: string) {
    const words = confirmWords(action.kind);
    setPending(action);
    setConfirmSubject({
      heading: words.heading(subscriber),
      body: words.body,
      commitLabel: words.commit,
      intent: 'danger',
    });
  }

  function dismiss() {
    setPending(null);
    setConfirmSubject(null);
  }

  /**
   * Run the ending the operator confirmed.
   *
   * The toast is raised on the service's answer and never before it: both writes
   * end a person's access, and a surface that says «انتهى الوصول» before the
   * money service agreed has told the operator something it does not know.
   */
  function commit() {
    if (!pending) return;

    const onError = (error: unknown) => {
      dismiss();
      const status = error instanceof ApiError ? error.status : 0;
      const code = error instanceof ApiError ? error.code : undefined;
      toast.show(resolveErrorCopy(code, status, errors).message, 'danger');
    };
    const onSuccess = (message: string) => () => {
      dismiss();
      toast.show(message, 'success');
    };

    if (pending.kind === 'revoke') {
      revokeEntitlement.mutate(pending.entitlementId, {
        onSuccess: onSuccess(copy.toast.revoked),
        onError,
      });
      return;
    }

    cancelSubscription.mutate(
      { subscriptionId: pending.subscriptionId },
      { onSuccess: onSuccess(copy.toast.cancelledNow), onError },
    );
  }

  const narrowed = hasAnyFilter(filters);

  return (
    <section aria-labelledby="ledger-heading" className="mt-section-gap">
      <h2 id="ledger-heading" className="mb-3 font-display text-h2 font-bold">
        {copy.ledger.heading}
      </h2>

      {/*
        The note is read **before** the table, not after it.

        It sat under the pager, which is where a reader arrives last and where a
        long page means they never arrive at all. Both sentences are things the
        operator needs on the way in: that a page of this table does not add up
        to the band above it — the first thing anyone reconciling the two would
        otherwise trip over — and that the state column is not editable, which is
        a claim about a control they are about to reach for. Under the table
        both facts are corrections; above it they are instructions.
      */}
      <LedgerNotes />

      <LedgerFilterBar
        filters={filters}
        plans={plans.data?.items ?? []}
        onChange={applyFilter}
        onClear={clearFilters}
        // A refetch, not the first load: the ledger's own pending state already
        // announces that, and «جارٍ البحث» on an arrival nobody searched from is
        // a statement about something that did not happen.
        busy={query.isFetching && !query.isPending}
      />

      {query.isError ? (
        <SurfaceError
          error={query.error}
          catalog={errors}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: subscriptionsKey() })}
        />
      ) : null}

      {query.isPending ? (
        <>
          <p role="status" className="sr-only">
            {copy.states.loadingLedger}
          </p>
          <LedgerTable>
            <RowSkeleton columns={TABLE_COLUMNS} />
          </LedgerTable>
        </>
      ) : null}

      {/*
        Stated above the rows it produced, because a searched ledger is ambiguous
        in the one way that matters: no rows can mean «this person holds nothing»
        or «nobody is called that», and the two send an operator to different
        places entirely.
      */}
      {query.data && query.data.matched_accounts !== null ? (
        <TermSummary matched={query.data.matched_accounts} truncated={query.data.matches_truncated} />
      ) : null}

      {query.data ? (
        rows.length === 0 ? (
          <EmptyState
            message={emptyMessage(narrowed, query.data.matched_accounts)}
            action={
              narrowed ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="hit-target flex items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
                >
                  {copy.states.emptyFilteredAction}
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
            <LedgerTable>
              {rows.map((row) => (
                // A manual grant is an entitlement, not a subscription, and
                // carries no `subscription_id`; the account and the moment it
                // was granted identify it between them.
                <LedgerTableRow
                  key={row.subscription_id ?? row.entitlement_id ?? `${row.user_id}-${row.created_at}`}
                  row={row}
                  onAct={ask}
                />
              ))}
            </LedgerTable>

            <Pager
              label={copy.ledger.pager.label}
              page={query.data.current_page}
              pageCount={pageCount}
              shown={rows.length}
              total={query.data.total}
              noun={{
                one: copy.ledger.pager.shownOne,
                two: copy.ledger.pager.shownTwo,
                few: copy.ledger.pager.shownFew,
                many: copy.ledger.pager.shownMany,
              }}
              hasPrevious={query.data.current_page > 1}
              hasNext={query.data.has_next}
              onPrevious={() => goToPage(query.data.current_page - 1)}
              onNext={() => goToPage(query.data.current_page + 1)}
              onGoToPage={goToPage}
            />
          </>
        )
      ) : null}

      {/*
        One dialog for the table, not one per row: the heading, body and commit
        label are written from the entry that opened it, so the words stay
        together in `copy.ts` where a reviewer reads them side by side.
      */}
      <ConfirmDialog
        open={confirmSubject !== null}
        subject={confirmSubject}
        busy={cancelSubscription.isPending || revokeEntitlement.isPending}
        onConfirm={commit}
        onCancel={dismiss}
      />
    </section>
  );
}

/** The three sentences one ending needs. A lift and a cancellation differ on all three. */
function confirmWords(kind: LedgerAction['kind']) {
  return kind === 'revoke' ? copy.confirm.revoke : copy.confirm.cancelNow;
}

/**
 * The entries one record actually has.
 *
 * One ending each, and they differ only in which table they address: a sale is
 * cancelled and a grant is lifted. The second entry a sale used to carry —
 * «أوقف التجديد» — went with automatic renewal, because a subscription left
 * alone already runs to the end of the period that was paid for.
 *
 * A `PENDING` row keeps its entry rather than losing it: an unconfirmed checkout
 * holds the one-live-subscription slot, and ending it is how an operator frees
 * that slot for the subscriber to try again.
 *
 * An **ended** record returns nothing at all, and the caller draws no handle.
 * That is not the console's visible-refusal case: refusal keeps the control
 * because hiding it cannot distinguish «not allowed» from «not shown here», and
 * here neither is true — the access ended on its own and there is no action to
 * refuse. The state badge in the row beside it is the whole explanation.
 */
function rowEntries(
  row: LedgerRow,
  onAct: (action: LedgerAction, subscriber: string) => void,
): { entries: RowMenuEntry[]; hint?: string } {
  if (ENDED_STATES.has(row.state)) return { entries: [] };

  if (row.source === MANUAL_GRANT) {
    // No id, no action. A ledger served by a service that predates the column
    // renders the row and offers nothing, rather than sending a request to a
    // path with `undefined` in it.
    if (!row.entitlement_id) return { entries: [] };
    const entitlementId = row.entitlement_id;

    return {
      entries: [
        {
          key: 'revoke',
          label: copy.rowMenu.revoke,
          danger: true,
          onSelect: () => onAct({ kind: 'revoke', entitlementId }, row.user_display_name),
        },
      ],
    };
  }

  if (!row.subscription_id) return { entries: [] };
  const subscriptionId = row.subscription_id;

  return {
    entries: [
      {
        key: 'cancel-now',
        label: copy.rowMenu.cancelNow,
        danger: true,
        onSelect: () => onAct({ kind: 'cancel-now', subscriptionId }, row.user_display_name),
      },
    ],
  };
}

/**
 * Which nothing this is.
 *
 * Three states share one screen and send an operator three different places: no
 * subscription has ever been opened; the narrowing excludes every one that has;
 * or accounts matched the term and none of them holds anything. A single
 * sentence for all three would be wrong twice.
 *
 * A term that matched **nobody** takes the narrowing wording rather than a fourth
 * string — the line above the table has already said that no account matches, and
 * repeating it under an empty table says it twice and adds nothing.
 */
function emptyMessage(narrowed: boolean, matched: number | null): string {
  if (!narrowed) return copy.states.emptyLedger;
  if (matched !== null && matched > 0) return copy.states.emptyMatched;
  return copy.states.emptyFiltered;
}

/**
 * The two facts an operator acts on, in one block rather than two loose lines,
 * and **before** the rows rather than after them.
 *
 * They were drawn as two bare paragraphs of different widths hanging off the
 * bottom of the table — one of them carrying the row count, which the pager now
 * states — and read as leftovers rather than as a note. One bordered block, one
 * measure, one leading: where the figures above come from, and where an
 * entitlement is actually granted or lifted.
 *
 * Position is the second half of the fix. A caveat under a paged table is read
 * by whoever scrolls to the end of it, which is nobody who found their row on
 * the third line; both sentences are preconditions for reading the table
 * correctly, so they go where the reader already is.
 *
 * **Neither sentence is decoration.** The tiles above count every subscription
 * in the service and a page of this table counts ten, so an operator reconciling
 * the two would otherwise find a difference and no explanation for it; and the
 * state column looks editable — it is a badge on a row in a console full of
 * editable rows — while the entitlement lifecycle is the only thing that writes
 * it.
 */
function LedgerNotes() {
  return (
    /*
      A sunken ground with a rule down its reading edge, rather than the raised
      panel it used to be. The lookup bar directly beneath it is now a raised
      surface too, and two identical panels stacked read as one control block
      with a paragraph accidentally inside it. Sunken is the console's «this is
      said, not operated» ground, and the `border-s` is the mark that carries
      that without a colour that would imply a state.
    */
    <div className="leading-relaxed mb-4 grid gap-2 rounded-md border border-s-2 border-border border-s-border-strong bg-bg-sunken p-4 text-xs text-ink-muted">
      {/*
        The **block** runs the width of the section and the **lines** are capped
        at the measure, rather than the block being capped.

        A capped block stopped two-thirds of the way across and left a ragged
        edge against the full-width lookup panel below it and the table below
        that — three stacked elements, two edges. Reading width is a property of
        the line, not of the surface it sits on, so the cap belongs on the
        paragraph.
      */}
      <p className="mb-0 max-w-measure">
        {copy.ledger.pageNoteLead}{' '}
        <Link to="/users" className="text-accent-text">
          {copy.ledger.usersLink}
        </Link>
        .
      </p>

      <p className="mb-0 max-w-measure">
        {copy.ledger.stateNoteLead}{' '}
        <Link to="/users" className="text-accent-text">
          {copy.ledger.usersLink}
        </Link>
        .
      </p>
    </div>
  );
}

function LedgerTableRow({
  row,
  onAct,
}: {
  row: LedgerRow;
  onAct: (action: LedgerAction, subscriber: string) => void;
}) {
  const state = resolveSubscriptionState(row.state);
  const { entries, hint } = rowEntries(row, onAct);

  return (
    <tr>
      <DTableRowHeader>
        {/*
          The subscriber is a link, because every question this row raises is
          answered on their account: what else they hold, whether they are
          blocked, what they have opened a case about. The ledger had no route to
          any of it — the register it points readers at has no search, so an
          operator who could see a row could not reach the person in it.
        */}
        <Link to={`/users/${encodeURIComponent(row.user_id)}`} className="block text-accent-text">
          {row.user_display_name}
        </Link>
        {/*
          The number is a **credential**, and that is an argument about what is
          stored and sent — not about what is drawn. Stored and sent it stays
          ASCII E.164; drawn in an Arabic table it is a human surface like any
          other, so it takes Arabic-Indic digits and the local grouping an
          operator recognises, exactly as the account register beside it does.
          The `.mono` class goes with the ASCII form and leaves with it: the
          face is Latin-only, and Arabic-Indic numerals set in it fall back to
          another family mid-line.
        */}
        {row.user_contact_number ? (
          <span className="mt-1 block text-xs text-ink-muted">
            <bdi>{formatPhoneForReading(row.user_contact_number)}</bdi>
          </span>
        ) : null}
      </DTableRowHeader>

      <Absence value={row.plan_name} label={copy.ledger.noPlan} />

      <td>{resolveEntitlementSource(row.source)}</td>

      {row.amount !== null && row.currency ? (
        <td className="whitespace-nowrap">
          <bdi>{formatMoney(row.amount, { currency: row.currency })}</bdi>
        </td>
      ) : (
        <td className="whitespace-nowrap text-ink-muted">
          —<span className="sr-only">{copy.ledger.noAmount}</span>
        </td>
      )}

      <td className="whitespace-nowrap">
        {/* Human string and machine attribute on the same element, one in each digit set. */}
        <time dateTime={toMachineDate(row.created_at)}>
          <bdi>{formatDate(row.created_at)}</bdi>
        </time>
      </td>

      <td>
        <StateBadge kind={state.kind}>{state.label}</StateBadge>
      </td>

      {/*
        The destructive control is never in the row: it lives in the row menu and
        the confirmation after it names the account. A cell with no entries is
        empty rather than absent, so every row still has seven cells and the
        column keeps its width down the table.
      */}
      <td className="w-[3.5rem]">
        {entries.length > 0 ? (
          <RowMenu label={copy.rowMenu.label(row.user_display_name)} entries={entries} hint={hint} />
        ) : null}
      </td>
    </tr>
  );
}

/** A cell whose value may legitimately be absent, and which states the absence. */
function Absence({ value, label }: { value: string | null; label: string }) {
  if (value) return <td>{value}</td>;

  return (
    <td className="text-ink-muted">
      —<span className="sr-only">{label}</span>
    </td>
  );
}

function LedgerTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.ledger.caption} minInlineSize="50rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.ledger.columns.subscriber}</th>
            <th scope="col">{copy.ledger.columns.plan}</th>
            <th scope="col">{copy.ledger.columns.source}</th>
            <th scope="col">{copy.ledger.columns.amount}</th>
            <th scope="col">{copy.ledger.columns.date}</th>
            <th scope="col">{copy.ledger.columns.state}</th>
            {/*
              A column header names what its cells hold, and a menu cell holds
              nothing that can be named. The label is visually hidden rather than
              omitted: an empty `<th>` leaves the column with no accessible name
              at all, which is what a screen-reader user meets when they walk the
              header row.
            */}
            <th scope="col">
              <span className="sr-only">{copy.ledger.columns.actions}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}
